import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWorkspace,
  updateWorkspace,
  acceptDpa,
  getErrorMessage,
  CURRENT_DPA_VERSION,
  type ControllerEntity,
} from '@/lib/api';
import { PageHeader, Icon, Badge, Loading } from '@/components/ui';

const PLATFORM_CAP = 1825; // 5 ans — borne dure plateforme (cf. WorkspacesService).

/** Affiche un nombre de jours en équivalent lisible (« ≈ 5 ans »). */
function humanDays(days: number): string {
  if (days >= 365) {
    const years = Math.round((days / 365) * 10) / 10;
    return `≈ ${years} an${years > 1 ? 's' : ''}`;
  }
  const months = Math.round(days / 30);
  if (months >= 12) return '≈ 1 an';
  if (months >= 1) return `≈ ${months} mois`;
  return `${days} jour${days > 1 ? 's' : ''}`;
}

/** Construit un ControllerEntity en ne gardant que les champs renseignés. */
function cleanEntity(e: Record<keyof ControllerEntity, string>): ControllerEntity {
  const out: ControllerEntity = {};
  if (e.name.trim()) out.name = e.name.trim();
  if (e.contact.trim()) out.contact = e.contact.trim();
  if (e.dpoName.trim()) out.dpoName = e.dpoName.trim();
  if (e.dpoEmail.trim()) out.dpoEmail = e.dpoEmail.trim();
  return out;
}

/**
 * Paramètres workspace (B8). Trois sections RGPD :
 *  - Entité responsable du traitement (art. 4 §7) — affichée au répondant.
 *  - Plafond de conservation (purge automatique).
 *  - Accord de traitement (DPA) : version + re-signature.
 * Écriture réservée au rôle `owner` (backend @Roles('owner') ; l'UI reflète).
 */
export default function WorkspaceSettingsPage(): JSX.Element {
  const { id: workspaceId } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const settingsQ = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => getWorkspace(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
    refetchOnWindowFocus: false,
  });

  const [entity, setEntity] = useState<Record<keyof ControllerEntity, string>>({
    name: '', contact: '', dpoName: '', dpoEmail: '',
  });
  const [retention, setRetention] = useState<number>(PLATFORM_CAP);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Synchronise l'état local quand les données arrivent (ou après une sauvegarde).
  useEffect(() => {
    const s = settingsQ.data;
    if (!s) return;
    const ce = (s.controllerEntity ?? {}) as ControllerEntity;
    setEntity({
      name: ce.name ?? '',
      contact: ce.contact ?? '',
      dpoName: ce.dpoName ?? '',
      dpoEmail: ce.dpoEmail ?? '',
    });
    setRetention(s.retentionDaysCap);
  }, [settingsQ.data]);

  const invalidate = (): Promise<unknown> =>
    qc.invalidateQueries({ queryKey: ['workspace', workspaceId] });

  const entityMut = useMutation({
    mutationFn: () => updateWorkspace(workspaceId ?? '', { controllerEntity: cleanEntity(entity) }),
    onSuccess: () => { setFeedback('Entité responsable enregistrée.'); setFormError(null); void invalidate(); },
    onError: (err) => { setFormError(getErrorMessage(err)); setFeedback(null); },
  });

  const retentionMut = useMutation({
    mutationFn: () => updateWorkspace(workspaceId ?? '', { retentionDaysCap: retention }),
    onSuccess: () => { setFeedback('Plafond de conservation enregistré.'); setFormError(null); void invalidate(); },
    onError: (err) => { setFormError(getErrorMessage(err)); setFeedback(null); },
  });

  const dpaMut = useMutation({
    mutationFn: () => acceptDpa(workspaceId ?? '', CURRENT_DPA_VERSION),
    onSuccess: () => { setFeedback('Nouvelle version du DPA signée.'); setFormError(null); void invalidate(); },
    onError: (err) => { setFormError(getErrorMessage(err)); setFeedback(null); },
  });

  if (!workspaceId) return <p>Workspace introuvable.</p>;
  if (settingsQ.isPending) return <div className="card card-flush"><div className="card-section"><Loading /></div></div>;
  if (settingsQ.isError || !settingsQ.data) {
    return <p role="alert" className="field-error"><Icon.AlertTriangle /><span>{getErrorMessage(settingsQ.error)}</span></p>;
  }

  const s = settingsQ.data;
  const canEdit = s.role === 'owner';
  const dpaOutdated = s.dpaVersion !== CURRENT_DPA_VERSION;
  const busy = entityMut.isPending || retentionMut.isPending || dpaMut.isPending;

  return (
    <>
      <PageHeader
        title="Paramètres du workspace"
        subtitle="Responsable de traitement, durée de conservation et accord de traitement (DPA). Ces informations encadrent la conformité RGPD de toutes vos enquêtes."
        back={{ to: `/workspaces/${workspaceId}/surveys`, label: 'Retour aux enquêtes' }}
      />

      {!canEdit && (
        <p className="notice flex items-start gap-2 mb-4">
          <Icon.Lock />
          <span>Votre rôle <strong>{s.role}</strong> est en lecture seule. Seul un propriétaire peut modifier ces paramètres.</span>
        </p>
      )}
      {feedback && (
        <p role="status" className="mb-4 flex items-center gap-2 text-sm text-success-text">
          <Icon.Check /> {feedback}
        </p>
      )}
      {formError && (
        <p role="alert" className="field-error mb-4"><Icon.AlertTriangle /><span>{formError}</span></p>
      )}

      {/* 1. Entité responsable du traitement */}
      <section className="card card-flush mb-6">
        <header className="card-header">
          <div className="flex items-center gap-2">
            <Icon.Shield className="text-primary" />
            <h2 className="text-base font-semibold">Entité responsable du traitement</h2>
          </div>
        </header>
        <form
          className="card-section grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => { e.preventDefault(); if (canEdit) entityMut.mutate(); }}
        >
          <p className="sm:col-span-2 text-sm text-text-muted">
            Identité du contrôleur (RGPD art. 4 §7) et de son DPO. Ces coordonnées sont
            affichées aux répondants dans le bandeau d'information de chaque enquête.
          </p>
          <div className="field">
            <label htmlFor="ce-name" className="field-label">Nom de l'entité responsable</label>
            <input id="ce-name" className="input-base" disabled={!canEdit} value={entity.name}
              placeholder="Université / Laboratoire / Association"
              onChange={(e) => setEntity((v) => ({ ...v, name: e.target.value }))} />
          </div>
          <div className="field">
            <label htmlFor="ce-contact" className="field-label">Contact (e-mail ou adresse postale)</label>
            <input id="ce-contact" className="input-base" disabled={!canEdit} value={entity.contact}
              placeholder="contact@institution.fr"
              onChange={(e) => setEntity((v) => ({ ...v, contact: e.target.value }))} />
          </div>
          <div className="field">
            <label htmlFor="ce-dpo" className="field-label">Nom du DPO</label>
            <input id="ce-dpo" className="input-base" disabled={!canEdit} value={entity.dpoName}
              placeholder="Délégué à la protection des données"
              onChange={(e) => setEntity((v) => ({ ...v, dpoName: e.target.value }))} />
          </div>
          <div className="field">
            <label htmlFor="ce-dpo-email" className="field-label">E-mail du DPO</label>
            <input id="ce-dpo-email" type="email" className="input-base" disabled={!canEdit} value={entity.dpoEmail}
              placeholder="dpo@institution.fr"
              onChange={(e) => setEntity((v) => ({ ...v, dpoEmail: e.target.value }))} />
          </div>
          {canEdit && (
            <div className="sm:col-span-2">
              <button type="submit" className="btn-primary" disabled={busy}>
                {entityMut.isPending ? 'Enregistrement…' : 'Enregistrer l’entité responsable'}
              </button>
            </div>
          )}
        </form>
      </section>

      {/* 2. Conservation des données */}
      <section className="card card-flush mb-6">
        <header className="card-header">
          <div className="flex items-center gap-2">
            <Icon.Settings className="text-primary" />
            <h2 className="text-base font-semibold">Conservation des données</h2>
          </div>
        </header>
        <form
          className="card-section"
          onSubmit={(e) => { e.preventDefault(); if (canEdit) retentionMut.mutate(); }}
        >
          <p className="text-sm text-text-muted mb-4">
            Plafond appliqué à <strong>toutes les enquêtes</strong> du workspace : une enquête ne
            peut pas conserver ses réponses au-delà de cette durée. Une purge automatique
            quotidienne <strong>supprime définitivement</strong> les réponses expirées (aucun
            archivage). Maximum plateforme : {PLATFORM_CAP} jours ({humanDays(PLATFORM_CAP)}).
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="field">
              <label htmlFor="retention" className="field-label">Plafond de conservation (jours)</label>
              <input id="retention" type="number" min={1} max={PLATFORM_CAP} step={1}
                className="input-base w-40" disabled={!canEdit} value={retention}
                onChange={(e) => setRetention(Math.max(1, Math.min(PLATFORM_CAP, Number(e.target.value) || 1)))} />
            </div>
            <Badge tone="neutral">{humanDays(retention)}</Badge>
            {canEdit && (
              <button type="submit" className="btn-primary" disabled={busy}>
                {retentionMut.isPending ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            )}
          </div>
        </form>
      </section>

      {/* 3. Accord de traitement (DPA) */}
      <section className="card card-flush">
        <header className="card-header">
          <div className="flex items-center gap-2">
            <Icon.Lock className="text-primary" />
            <h2 className="text-base font-semibold">Accord de traitement des données (DPA)</h2>
          </div>
        </header>
        <div className="card-section">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm">Version signée :</span>
            <Badge tone="primary">v{s.dpaVersion}</Badge>
            <span className="text-sm text-text-muted">
              le {new Date(s.dpaAcceptedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
            {dpaOutdated
              ? <Badge tone="notice">Mise à jour disponible (v{CURRENT_DPA_VERSION})</Badge>
              : <Badge tone="success">À jour</Badge>}
          </div>
          {dpaOutdated && canEdit && (
            <>
              <p className="notice flex items-start gap-2 mb-3">
                <Icon.AlertTriangle />
                <span>Une nouvelle version du DPA (v{CURRENT_DPA_VERSION}) est disponible. La re-signature
                  est requise pour rester conforme. Action réservée au propriétaire et tracée.</span>
              </p>
              <button type="button" className="btn-primary" disabled={busy} onClick={() => dpaMut.mutate()}>
                {dpaMut.isPending ? 'Signature…' : `Signer la version v${CURRENT_DPA_VERSION}`}
              </button>
            </>
          )}
          {!dpaOutdated && (
            <p className="text-sm text-text-muted">
              Le DPA en vigueur est signé. La signature initiale est obligatoire à la création du
              workspace ; les nouvelles versions doivent être re-signées par le propriétaire.
            </p>
          )}
        </div>
      </section>
    </>
  );
}
